import classNames from 'classnames';
import { Link, graphql, useStaticQuery } from 'gatsby';
import React, { FC } from 'react';
import { MdOutlineErrorOutline } from 'react-icons/md';

import {
  useGetPlayerOverviewPageLink,
  useGetPlayerRiskAssessmentOverviewPageLink,
} from '@/bits/links/useLink';
import {
  Card,
  CardBody,
  CardCloseButton,
  CardOptions,
  CardOptionsButton,
  ControlledModal,
  InlineIconButton,
  Table,
} from '@/components';
import { useDataGrid } from '@/components/DataGrid';
import {
  DocumentDownloadIcon,
  EyeIcon,
  NextIcon,
  PreviousIcon,
  RefreshIcon,
} from '@/components/icons';
import { useTranslate } from '@/contexts';
import {
  BrandEnum,
  ReviewStatus,
  RiskAggregationOrderBy,
  RiskLevel,
} from '@/globalTypes';
import { getRiskLevelColor } from '@/utils';
import formatDate from '@/utils/formatter/formatDate';
import useAmlRiskAssessmentReport from './useAmlRiskAssessmentReport';
import useRiskAssessment, { MAX_FINAL_SCORE } from './useRiskAssessment';

const BLOCK_STATIC_QUERY = graphql`
  query SanityRiskAssessmentBlockStaticQuery {
    sanityRiskAssessmentBlock {
      title {
        ...SanityLocaleString
      }
      playerGlobalId {
        ...SanityLocaleString
      }
      brands {
        ...SanityLocaleString
      }
      finalScore {
        ...SanityLocaleString
      }
      risk {
        ...SanityLocaleString
      }
      kycStatus {
        ...SanityLocaleString
      }
      calculatedAt {
        ...SanityLocaleString
      }
      reviewStatus {
        ...SanityLocaleString
      }
      lastReviewedDate {
        ...SanityLocaleString
      }
    }
  }
`;

const RiskAssessmentBlock: FC = () => {
  const { t } = useTranslate();
  const getPlayerOverviewLink = useGetPlayerOverviewPageLink();
  const getPlayerRiskAssessmentOverviewPageLink =
    useGetPlayerRiskAssessmentOverviewPageLink();

  const block =
    useStaticQuery<Queries.SanityRiskAssessmentBlockStaticQueryQuery>(
      BLOCK_STATIC_QUERY,
    ).sanityRiskAssessmentBlock;

  const {
    entries,
    fetching,
    nextPage,
    previousPage,
    refresh,
    orderBy,
    desc,
    setOrderBy,
    defaultFilter,
    updateFilter,
  } = useRiskAssessment();

  const report = useAmlRiskAssessmentReport();

  const { DataGrid, GlobalCardOptions } = useDataGrid({
    name: 'riskAssessments',
    data: entries,
    fetching,
    orderBy,
    setOrderBy,
    desc,
    defaultFilter,
    updateFilter,
    schema: (s) => [
      s.stringValue({
        field: 'playerGlobalId',
        title: t(block?.playerGlobalId),
        filterField: 'playerGlobalId',
        linkTo: ({ row }) =>
          (row?.playerGlobalId &&
            getPlayerRiskAssessmentOverviewPageLink(row.playerGlobalId)) ??
          '',
      }),
      s.enumValue({
        field: 'brand',
        title: t(block?.brands),
        e: BrandEnum,
        wrap: ({ row }) => (
          <div className="flex items-center">
            {row.players && row.players.length < 3 ? (
              <span>
                {row.players?.map((player) => player.brand.name).join(', ')}
              </span>
            ) : (
              <>
                <span className="text-blue-600">{row.players?.length}</span>
                <ControlledModal
                  content={
                    <Card
                      title={t(block?.brands)}
                      size="lg"
                      options={
                        <CardOptions>
                          <CardCloseButton />
                        </CardOptions>
                      }
                    >
                      <div className="p-3">
                        <Table
                          headings={['Player ID', 'Brand']}
                          rows={
                            row.players?.map((player) => ({
                              key: player.id,
                              data: [
                                <Link
                                  className="w-full h-full p-3 block"
                                  to={getPlayerOverviewLink(player.id) ?? ''}
                                >
                                  {player.rawPlayerId}
                                </Link>,
                                player.brand.name,
                              ],
                            })) ?? []
                          }
                        />
                      </div>
                    </Card>
                  }
                >
                  <InlineIconButton>
                    <EyeIcon className="text-gray-400 text-lg" />
                  </InlineIconButton>
                </ControlledModal>
              </>
            )}
          </div>
        ),
      }),
      s.numberValue({
        field: 'finalScore',
        title: t(block?.finalScore),
        fromFilterField: 'finalScoreFrom',
        toFilterField: 'finalScoreTo',
        orderBy: RiskAggregationOrderBy.finalScore,
        maxToFilter: MAX_FINAL_SCORE,
      }),
      s.enumValue({
        field: 'riskLevel',
        title: t(block?.risk),
        filterField: 'riskLevels',
        isMulti: true,
        e: RiskLevel,
        orderBy: RiskAggregationOrderBy.riskLevel,
        wrap: ({ row }) => (
          <span>
            <MdOutlineErrorOutline
              className={classNames(
                'inline-block',
                'mr-2',
                'text-lg',
                getRiskLevelColor(row.riskLevel),
              )}
            />
            {row.riskLevel}
          </span>
        ),
      }),
      s.stringValue({
        field: 'kycCheckStatus.status',
        title: t(block?.kycStatus),
        getValue: ({ row }) => row.kycCheckStatus?.status,
      }),
      s.dateTimeValue({
        field: 'calculatedAt',
        title: t(block?.calculatedAt),
        fromFilterField: 'from',
        toFilterField: 'to',
        orderBy: RiskAggregationOrderBy.calculatedAt,
        includeTime: true,
        includeTimeInFilter: true,
      }),
      s.enumValue({
        field: 'reviewStatusInfo.status',
        title: t(block?.reviewStatus),
        filterField: 'reviewStatus',
        e: ReviewStatus,
        getValue: ({ row }) => row.reviewStatusInfo?.status,
      }),
      s.dateTimeValue({
        field: 'reviewStatusInfo.lastReviewed',
        title: t(block?.lastReviewedDate),
        fromFilterField: 'lastReviewedFrom',
        toFilterField: 'lastReviewedTo',
        getValue: ({ row }) => formatDate(row.reviewStatusInfo?.lastReviewed),
      }),
    ],
  });

  if (!block) {
    return null;
  }

  return (
    <Card
      size="lg"
      title={t(block.title)}
      showOptionsAtBottom
      options={
        <CardOptions>
          <CardOptionsButton
            disabled={!previousPage}
            onClick={() => previousPage && previousPage()}
          >
            <PreviousIcon />
          </CardOptionsButton>
          <CardOptionsButton
            disabled={!nextPage}
            onClick={() => nextPage && nextPage()}
          >
            <NextIcon />
          </CardOptionsButton>
          <GlobalCardOptions />
          <CardOptionsButton
            onClick={() => refresh({ requestPolicy: 'network-only' })}
          >
            <RefreshIcon />
          </CardOptionsButton>
          <CardOptionsButton
            disabled={report.fetching}
            onClick={() => report.generate()}
          >
            <DocumentDownloadIcon />
          </CardOptionsButton>
        </CardOptions>
      }
    >
      <CardBody>
        <DataGrid />
      </CardBody>
    </Card>
  );
};

export default RiskAssessmentBlock;
